import React, {useState} from 'react';
import {createStyles, makeStyles} from "@mui/styles";
import {Theme} from "@mui/material/styles";
import Typography from '@mui/material/Typography';
import {MenuItem, Select, Step, StepContent, StepLabel, Stepper} from '@mui/material';
import CommonCssStyles from "../../../../css/CommonCssStyles";
import {Configuration, ConfigurationId} from "./Configuration";
import Button from "@mui/material/Button";
import {ExportConfigurationNullable} from "../../../../common/Types";
import {t} from "../../../../store/localization/TranslMessasge";
import {ExportTranslationKey} from "./ExportTranslationKey";
import {useParams} from "react-router-dom";
import Page from "../../../../components/page/Page";
import PageHeader from "../../../../components/page/PageHeader";
import PageTitle from "../../../../components/page/PageTitle";
import PageContent from "../../../../components/page/PageContent";

const INITIAL_STEP = 0;
const CONFIGURATION_STEP = 1;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        exportTypeSelect: {
            minWidth: "12em",
        },
        exportTypeBlock: {
            "& .MuiButton-label": {
                fontSize: "0.7em",
            },
            display: "inline-block",
            fontSize: "0.9em",
            marginLeft: theme.spacing(2),
        },
        exportTypeDiv: {
            paddingTop: theme.spacing(2),
        },
        stepperPage: {
            display: "flex",
        },
        stepperDiv: {
            "& .MuiStepper-root": {
                padding: 0,
                borderRight: "1px solid lightgray",
            },
            display: "inline-block",
            minWidth: "23em",
        },
        stepContentDiv: {
            display: "inline-block",
            paddingLeft: theme.spacing(CommonCssStyles.PAGE_SEGMENT_PADDING),
            flexGrow: 1,
        }
    })
);

export default function ExportPage() {

    const configurations = Configuration.getConfigurations();
    const classes = useStyles();

    const {initialConfigurationId} = useParams();

    const [selectedConfiguration, setSelectedConfiguration] = useState<ExportConfigurationNullable>(getInitialConfiguration());
    const [activeStep, setActiveStep] = useState<number>(selectedConfiguration ? CONFIGURATION_STEP : INITIAL_STEP);

    function getInitialConfiguration(): ExportConfigurationNullable {
        return initialConfigurationId ? Configuration.findById(configurations, initialConfigurationId as (ConfigurationId)) : null;
    }

    function updateSelectedConfiguration(configurationId: number) {
        const configuration: ExportConfigurationNullable = configurationId === INITIAL_STEP ? null : configurations[configurationId - 1];
        const activeStep = configuration !== null ? CONFIGURATION_STEP : INITIAL_STEP;

        setSelectedConfiguration(configuration);
        setActiveStep(activeStep);
    }

    function createConfigurationId(index: number): number {
        return index + 1;
    }

    function resetExport() {
        setActiveStep(INITIAL_STEP);
    }

    const pageTitle = selectedConfiguration != null ? t(selectedConfiguration.pageLabelKey) : t(ExportTranslationKey.TITLE);

    return (
        <Page>
            <PageHeader>
                <PageTitle title={pageTitle} />
            </PageHeader>
            <PageContent>
                <div className={classes.stepperPage}>
                    <div className={classes.stepperDiv}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            <Step key={t(ExportTranslationKey.TYPE_LABEL)}>
                                <StepLabel>
                                    {t(ExportTranslationKey.TYPE_LABEL)}
                                </StepLabel>
                                <StepContent>
                                    {activeStep === INITIAL_STEP && <div>
                                        <Typography style={{fontSize: "0.9em"}}>{t(ExportTranslationKey.TYPE_DESCRIPTION)}</Typography>
                                        <div className={classes.exportTypeDiv}>
                                            <Select
                                                id="export-type-select"
                                                value={INITIAL_STEP}
                                                className={classes.exportTypeSelect}
                                                onChange={(event) => updateSelectedConfiguration(event.target.value as number)}
                                            >
                                                <MenuItem value={INITIAL_STEP} key={"initial_step"}>
                                                    <span>&nbsp;</span>
                                                </MenuItem>
                                                {
                                                    Configuration.getConfigurations().map((config, index) => <MenuItem value={createConfigurationId(index)} key={config.label}>{config.label}</MenuItem>)
                                                }
                                            </Select>
                                        </div>
                                    </div>}
                                </StepContent>
                            </Step>
                            <Step key={t(ExportTranslationKey.EXPORT_CONFIGURATION_LABEL)}>
                                <StepLabel>
                                    {t(ExportTranslationKey.EXPORT_CONFIGURATION_LABEL)}
                                </StepLabel>
                                <StepContent>
                                        <span>
                                            <Typography style={{fontSize: "0.9em"}} display={'inline'}>{pageTitle}</Typography>
                                            <span className={classes.exportTypeBlock}>
                                                <Button variant={"contained"}
                                                        size={"small"}
                                                        style={{fontSize: "inherit"}}
                                                        color={"primary"}
                                                        onClick={() => updateSelectedConfiguration(INITIAL_STEP)}>{t(ExportTranslationKey.CHANGE_EXPORT_TYPE)}</Button>
                                            </span>
                                        </span>
                                </StepContent>
                            </Step>
                        </Stepper>
                    </div>
                    <div className={classes.stepContentDiv}>
                        {activeStep !== INITIAL_STEP && <div>
                            {selectedConfiguration?.getWizard(() => resetExport())}
                        </div>}
                    </div>
                </div>
            </PageContent>
        </Page>
    );
}